import { VOICE_AGENT_VOICES } from "@jugl-web/rest-api/integrations";
import { CHAT_BOT_DEFAULT_SETTINGS } from "../consts";
import type { ChatBotSettings } from "../types";

/**
 * Sanitizes a raw settings object coming from an untrusted source
 * (API response, external website config, URL params, etc.).
 * The input is `unknown` because runtime data may be missing, malformed,
 * or manually modified. Always returns a fully valid `ChatBotSettings`
 * object with safe defaults applied for any missing or invalid fields.
 *
 */
export const resolveChatBotSettings = (input: unknown): ChatBotSettings => {
  const resolved: ChatBotSettings = structuredClone(CHAT_BOT_DEFAULT_SETTINGS);

  const dict =
    input !== null && typeof input === "object" && !Array.isArray(input)
      ? (input as Record<string, unknown>)
      : null;

  if (!dict) {
    return resolved;
  }

  if (typeof dict.chatName === "string") {
    resolved.chatName = dict.chatName;
  }

  if (typeof dict.chatImageUrl === "string") {
    resolved.chatImageUrl = dict.chatImageUrl;
  }

  if (typeof dict.agentName === "string") {
    resolved.agentName = dict.agentName;
  }

  if (typeof dict.agentImage === "string") {
    resolved.agentImage = dict.agentImage;
  }

  if (typeof dict.welcomeMessage === "string") {
    resolved.welcomeMessage = dict.welcomeMessage;
  }

  if (dict.theme === "light" || dict.theme === "dark") {
    resolved.theme = dict.theme;
  }

  if (typeof dict.senderMessageBackgroundColor === "string") {
    resolved.senderMessageBackgroundColor = dict.senderMessageBackgroundColor;
  }

  if (Array.isArray(dict.suggestions)) {
    resolved.suggestions = dict.suggestions.filter(
      (s): s is string => typeof s === "string"
    );
  }

  if (typeof dict.buttonIcon === "string") {
    resolved.buttonIcon = dict.buttonIcon;
  }

  if (typeof dict.buttonColor === "string") {
    resolved.buttonColor = dict.buttonColor;
  }

  if (dict.alignment === "right" || dict.alignment === "left") {
    resolved.alignment = dict.alignment;
  }

  if (typeof dict.sideSpacing === "number") {
    resolved.sideSpacing = dict.sideSpacing;
  }

  if (typeof dict.bottomSpacing === "number") {
    resolved.bottomSpacing = dict.bottomSpacing;
  }

  if (typeof dict.isButtonHidden === "boolean") {
    resolved.isButtonHidden = dict.isButtonHidden;
  }

  if (Array.isArray(dict.ctas)) {
    resolved.ctas = dict.ctas.filter(
      (cta): cta is string => typeof cta === "string"
    );
  }

  if (dict.ctaVisual === null) {
    resolved.ctaVisual = null;
  } else if (
    dict.ctaVisual &&
    typeof dict.ctaVisual === "object" &&
    !Array.isArray(dict.ctaVisual)
  ) {
    const raw = dict.ctaVisual as Record<string, unknown>;

    if (typeof raw.id === "string" && typeof raw.url === "string") {
      resolved.ctaVisual = { id: raw.id, url: raw.url };
    } else {
      resolved.ctaVisual = null;
    }
  }

  if (
    dict.voice_config &&
    typeof dict.voice_config === "object" &&
    !Array.isArray(dict.voice_config)
  ) {
    const raw = dict.voice_config as Record<string, unknown>;

    if (typeof raw.enabled === "boolean") {
      resolved.voice_config.enabled = raw.enabled;
    }

    const matchingVoice = VOICE_AGENT_VOICES.find(
      (voice) => voice === raw.voice
    );

    if (matchingVoice) {
      resolved.voice_config.voice = matchingVoice;
    }
  }

  return resolved;
};
